import type { FeedCostEstimate } from '@kanbu/schema/contracts';
import { FeedSource, UpdateFrequency } from '@kanbu/schema/enums';
import { getNextScheduledAt } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  Input,
  Label,
  Select,
  SelectItem,
  Switch,
  toast,
} from '@utima/ui';
import { memo, useEffect, useState } from 'react';

import { ConfirmDialog } from '@/components/confirmDialog/ConfirmDialog';
import { MultiSelect } from '@/components/multiselect/MultiSelect';
import { useEnumTranslations } from '@/hooks';
import { trpc } from '@/services/trpc';

import { getFeedAgents, useShopAgents } from './feedAgents';
import { useFeedMutation } from './useFeedMutation';

export type FeedSettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chatId: string;
  feedId?: string;
};

const formatRange = (range: { low: number; high: number }, digits = 2) =>
  `${range.low.toFixed(digits)}–${range.high.toFixed(digits)} USD`;

export const FeedSettingsModal = memo(function FeedSettingsModal({
  open,
  onOpenChange,
  chatId,
  feedId,
}: FeedSettingsModalProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const {
    create: { mutateAsync: create, isPending: isCreating },
    update: { mutateAsync: update, isPending: isUpdating },
    remove: { mutateAsync: remove },
  } = useFeedMutation();

  const isEdit = !!feedId;

  const { data: feed } = trpc.feeds.findOne.useQuery(
    { id: feedId!, populate: ['chats'] },
    { enabled: open && isEdit },
  );
  const { data: shopAgents } = useShopAgents(open);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [source, setSource] = useState<FeedSource>(FeedSource.Heureka);
  const [updateFrequency, setUpdateFrequency] = useState<UpdateFrequency>(
    UpdateFrequency.Monthly,
  );
  const [enrichProducts, setEnrichProducts] = useState(false);
  const [scrapeProductPages, setScrapeProductPages] = useState(false);
  const [annotateImages, setAnnotateImages] = useState(false);
  const [chatIds, setChatIds] = useState<string[]>([chatId]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [draftFeedId, setDraftFeedId] = useState<string>();
  const estimateCost = trpc.feeds.estimateCost.useMutation();
  const [estimate, setEstimate] = useState<{
    key: string;
    value: FeedCostEstimate;
  }>();
  const estimateKey = JSON.stringify([
    feedId,
    url.trim(),
    source,
    enrichProducts,
    scrapeProductPages,
    annotateImages,
  ]);
  const currentEstimate =
    estimate?.key === estimateKey ? estimate.value : undefined;

  const handleEstimate = async () => {
    try {
      const value = await estimateCost.mutateAsync({
        feedId,
        url: url.trim(),
        source,
        enrichProducts,
        scrapeProductPages,
        annotateImages,
      });
      setEstimate({ key: estimateKey, value });
    } catch {
      toast.error(
        t`Could not estimate this feed. Check its URL and selected format.`,
      );
    }
  };

  useEffect(() => {
    setConfirmDeleteOpen(false);
    setEstimate(undefined);
  }, [open, feedId]);

  useEffect(() => {
    if (!open) return;

    if (isEdit && feed && feed.id === feedId) {
      setName(feed.name);
      setUrl(feed.url);
      setSource(feed.source);
      setUpdateFrequency(feed.updateFrequency ?? UpdateFrequency.Monthly);
      setEnrichProducts(feed.enrichProducts);
      setScrapeProductPages(feed.scrapeProductPages);
      setAnnotateImages(feed.annotateImages);
      setChatIds(getFeedAgents(feed).map(agent => agent.id));
      setDraftFeedId(feedId);
    } else {
      setName('');
      setUrl('');
      setSource(FeedSource.Heureka);
      setUpdateFrequency(UpdateFrequency.Monthly);
      setEnrichProducts(false);
      setScrapeProductPages(false);
      setAnnotateImages(false);
      setChatIds([chatId]);
      setDraftFeedId(undefined);
    }
  }, [open, isEdit, feed, feedId, chatId]);

  const otherAgents = feed
    ? getFeedAgents(feed)
        .filter(agent => agent.id !== chatId)
        .map(agent => agent.name)
        .join(', ')
    : '';

  const isDraftReady =
    !isEdit || (feed?.id === feedId && draftFeedId === feedId);
  const canSave =
    isDraftReady &&
    name.trim().length > 0 &&
    url.trim().length > 0 &&
    chatIds.length > 0;

  const handleSave = async () => {
    if (!canSave || isCreating || isUpdating) return;

    const values = {
      name: name.trim(),
      url: url.trim(),
      source,
      updateFrequency,
      enrichProducts,
      scrapeProductPages,
      annotateImages,
      chatIds,
    };

    try {
      if (isEdit) {
        await update({
          id: feedId!,
          ...values,
          scheduledAt: getNextScheduledAt(updateFrequency),
        });
        toast.success(t`Feed updated`);
      } else {
        await create(values);
        toast.success(t`Feed created`);
      }

      onOpenChange(false);
    } catch (error) {
      toast.error(
        isEdit ? t`Failed to update feed` : t`Failed to create feed`,
        {
          description: error instanceof Error ? error.message : undefined,
        },
      );
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !isDraftReady) return;

    try {
      await remove({ id: feedId! });
      onOpenChange(false);
    } catch {
      toast.error(t`Failed to delete feed`);
    }
  };

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content className='max-h-[90vh] w-[600px] overflow-y-auto'>
          <Dialog.Header>
            <Dialog.Title>
              {isEdit ? (
                <Trans>Product feed settings</Trans>
              ) : (
                <Trans>New product feed</Trans>
              )}
            </Dialog.Title>
          </Dialog.Header>

          <div className='space-y-4 py-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='feedName'>
                <Trans>Name</Trans>
              </Label>
              <Input
                id='feedName'
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='feedUrl'>
                <Trans>Feed URL</Trans>
              </Label>
              <Input
                id='feedUrl'
                value={url}
                onChange={e => setUrl(e.target.value)}
              />
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='feedSource'>
                <Trans>Feed Source</Trans>
              </Label>
              <Select
                value={source}
                onValueChange={value => setSource(value as FeedSource)}
              >
                {Object.values(FeedSource).map(value => (
                  <SelectItem key={value} value={value}>
                    {enumTranslations.feedSource[value]}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div className='space-y-1.5'>
              <Label>
                <Trans>Agents</Trans>
              </Label>
              <MultiSelect
                value={chatIds}
                forcedDefaultValue={chatId}
                onChange={setChatIds}
                options={(shopAgents?.items ?? []).map(agent => ({
                  value: agent.id,
                  label: agent.name,
                }))}
                placeholder={t`Select agents`}
              />
              <p className='text-xs text-gray-500'>
                <Trans>
                  The feed is imported and billed once. Every agent using it
                  shares its settings and attributes. An agent whose feeds are
                  not all enriched searches without the enrichment for all of
                  them.
                </Trans>
              </p>
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='feedUpdateFrequency'>
                <Trans>Update Frequency</Trans>
              </Label>
              <Select
                value={updateFrequency}
                onValueChange={value =>
                  setUpdateFrequency(value as UpdateFrequency)
                }
              >
                {Object.values(UpdateFrequency).map(value => (
                  <SelectItem key={value} value={value}>
                    {enumTranslations.updateFrequency[value]}
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div className='space-y-3 rounded-md border border-border p-3'>
              <div className='flex items-center justify-between gap-4'>
                <div className='space-y-0.5'>
                  <Label htmlFor='feedEnrichProducts'>
                    <Trans>Enrich products with AI</Trans>
                  </Label>
                  <p className='text-xs text-gray-500'>
                    <Trans>
                      Adds a short summary and search phrases to each product.
                      Attributes stay as the shop provides them. Paid per
                      processed product.
                    </Trans>
                  </p>
                </div>
                <Switch
                  id='feedEnrichProducts'
                  checked={enrichProducts}
                  onCheckedChange={setEnrichProducts}
                />
              </div>
              <div className='flex items-center justify-between gap-4'>
                <div className='space-y-0.5'>
                  <Label htmlFor='feedScrapeProductPages'>
                    <Trans>Scrape product pages</Trans>
                  </Label>
                  <p className='text-xs text-gray-500'>
                    <Trans>
                      Fetches each product&apos;s page and feeds the text into
                      enrichment, so descriptions and search matching improve
                      for items this feed describes thinly. Costs a paid scrape
                      per product.
                    </Trans>
                  </p>
                </div>
                <Switch
                  id='feedScrapeProductPages'
                  disabled={!enrichProducts}
                  checked={scrapeProductPages}
                  onCheckedChange={setScrapeProductPages}
                />
              </div>

              <div className='flex items-center justify-between gap-4'>
                <div className='space-y-0.5'>
                  <Label htmlFor='feedAnnotateImages'>
                    <Trans>Annotate images</Trans>
                  </Label>
                  <p className='text-xs text-gray-500'>
                    <Trans>
                      Sends product images to a vision model during enrichment,
                      so products become findable by what they visibly are.
                      Costs vision tokens per product.
                    </Trans>
                  </p>
                </div>
                <Switch
                  id='feedAnnotateImages'
                  disabled={!enrichProducts}
                  checked={annotateImages}
                  onCheckedChange={setAnnotateImages}
                />
              </div>

              <p className='text-xs text-gray-500'>
                <Trans>
                  Changes apply on the next import. Scraping and image analysis
                  run only when enrichment is enabled. Turning enrichment off
                  removes generated metadata on the next import.
                </Trans>
              </p>
            </div>
            <div className='space-y-2 rounded-md border border-border p-3'>
              <div className='flex items-center justify-between gap-3'>
                <Label>
                  <Trans>Estimated import cost</Trans>
                </Label>
                <Button
                  variant='ghost'
                  outline
                  onClick={handleEstimate}
                  loading={estimateCost.isPending}
                  disabled={
                    !isDraftReady || !url.trim() || estimateCost.isPending
                  }
                >
                  <Trans>Estimate cost</Trans>
                </Button>
              </div>
              <p className='text-xs text-gray-500'>
                <Trans>
                  A full processing pass, billed in USD equivalent. Unchanged
                  refreshes usually cost less. This checks the feed without
                  importing products.
                </Trans>
              </p>
              {currentEstimate && (
                <div className='space-y-1 text-sm' aria-live='polite'>
                  <p>
                    <Trans>Products:</Trans> {currentEstimate.productCount}
                  </p>
                  <p>
                    <Trans>Enrichment per product:</Trans>{' '}
                    {formatRange(currentEstimate.perProduct.enrichment, 4)}
                  </p>
                  <p>
                    <Trans>Embeddings per product:</Trans>{' '}
                    {formatRange(currentEstimate.perProduct.embeddings, 4)}
                  </p>
                  <p>
                    <Trans>Scraping adds per product when enabled:</Trans>{' '}
                    {formatRange(currentEstimate.perProduct.scraping, 4)}
                  </p>
                  <p>
                    <Trans>Image analysis adds per product when enabled:</Trans>{' '}
                    {formatRange(currentEstimate.perProduct.images, 4)}
                  </p>
                  <p>
                    <Trans>Per product with selected options:</Trans>{' '}
                    {formatRange(currentEstimate.perProductTotal, 4)}
                  </p>
                  <p className='font-semibold'>
                    <Trans>Total with selected options:</Trans>{' '}
                    {formatRange(currentEstimate.total)}
                  </p>
                  <p className='text-xs text-gray-500'>
                    {currentEstimate.enrichmentBasis === 'history' ? (
                      <Trans>
                        Enrichment is calibrated from this feed’s recorded
                        usage.
                      </Trans>
                    ) : (
                      <Trans>
                        Uses estimated rates for the configured model; this feed
                        does not yet have comparable usage samples.
                      </Trans>
                    )}
                  </p>
                  <p className='text-xs text-gray-500'>
                    <Trans>
                      Estimate only: assumes one processing pass and one scrape
                      per product when enabled. Enrichment varies by roughly
                      ±50%; image analysis is modelled. Page length, retries and
                      model choice can increase the cost.
                    </Trans>
                  </p>
                </div>
              )}
            </div>
          </div>

          <Dialog.Footer>
            {isEdit && (
              <Button
                variant='danger'
                outline
                className='mr-auto'
                disabled={!isDraftReady}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                <Trans>Delete</Trans>
              </Button>
            )}
            <Button variant='ghost' outline onClick={() => onOpenChange(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              onClick={handleSave}
              loading={isCreating || isUpdating}
              disabled={!canSave}
            >
              <Trans>Save</Trans>
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>

      <ConfirmDialog
        open={confirmDeleteOpen && isDraftReady}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        description={
          otherAgents
            ? t`This feed is also used by ${otherAgents}. Deleting it removes its products from every agent. To stop using it only here, remove this agent from its agents instead.`
            : t`Are you sure you want to delete this feed?`
        }
      />
    </>
  );
});
